import { Formik } from 'formik';
import * as React from 'react';
import { FC, useState, useContext } from 'react';
import { TextInput, Button, Typography, PhoneInputField } from '@/components';
import {
  CheckboxField,
  Flex,
  Grid,
  Radio,
  RadioGroupField,
  View
} from '@aws-amplify/ui-react';
import { useStyles } from './styles';
import { AssetsContext } from '@/GlobalProvider/GlobalProvider';
import { convertAgeToDate, convertDateToAge } from '@/utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
  converFtToInches,
  convertInchesToFt,
  covertCmToFt,
  onePnInKg
} from './calculations.helper';
import { getSchema } from './schema';
import { useCreateUpdateCustomerForm } from './create-update-customer-form.helper';
import * as classNames from 'classnames';

export interface User {
  id?: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  dateOfBirth: number;
  gender: string;
  height: number;
  weight: number;
}

export interface UserCreateData extends User {
  heightMeasure: 'cm' | 'fnIn';
  weightMeasure: 'kgs' | 'lbs';
  termsAndConditions?: boolean;
}

const getInitialValues = (gender = 'male'): UserCreateData => ({
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  gender,
  height: null,
  heightMeasure: 'fnIn',
  weight: null,
  weightMeasure: 'lbs',
  termsAndConditions: false
});

interface CreateUpdateCustomerForm {
  submitHandler: (data: UserCreateData) => Promise<void>;
  isUpdateForm?: boolean;
  defaultValue?: User;
  isQuickUpdate?: boolean;
}

const getIsEmailRequired = (customer_account_handle: string) => {
  switch (customer_account_handle) {
    case 'email_only':
      return true;
    case 'email_required_phone_optional':
      return true;
    case 'email_and_phone':
      return true;
    default:
      return false;
  }
};

const getIsPhoneRequired = (customer_account_handle: string) => {
  switch (customer_account_handle) {
    case 'phone_only':
      return true;
    case 'email_and_phone':
      return true;
    default:
      return false;
  }
};

const singUpMessages = {
  email_or_phone: 'Phone number or email address are mandatory for sign up.',
  email_only: 'Email address is mandatory for sign up.',
  phone_only: 'Phone number is mandatory for sign up.',
  email_and_phone: 'Phone number or email address are mandatory for sign up.',
  email_required_phone_optional: 'Email address is mandatory for sign up.'
};

const getUpdateMessages = (hasPhone: boolean, hasEmail: boolean) => {
  if (hasPhone && hasEmail) {
    return {
      email_or_phone: 'Phone number and email address cannot be updated.',
      email_only: 'Email address cannot be updated.',
      phone_only: 'Phone number cannot be updated.',
      email_and_phone: 'Phone number and email address cannot be updated.',
      email_required_phone_optional:
        'Phone number and email address cannot be updated.'
    };
  }

  if (hasPhone) {
    return {
      email_or_phone: 'Phone number cannot be updated.',
      email_only: 'Email address cannot be updated.',
      phone_only: 'Phone number cannot be updated.',
      email_and_phone: 'Phone number cannot be updated.',
      email_required_phone_optional:
        'Phone number and email address cannot be updated.'
    };
  }

  if (hasEmail) {
    return {
      email_or_phone: 'Email address cannot be updated.',
      email_only: 'Email address cannot be updated.',
      phone_only: 'Phone number cannot be updated.',
      email_and_phone: 'Phone number and email address cannot be updated.',
      email_required_phone_optional: 'Email address cannot be updated.'
    };
  }

  return {};
};

const errorMessages = {
  email_or_phone: 'Phone number or email address is required',
  email_only: 'Email address is required',
  phone_only: 'Phone number is required',
  email_and_phone: 'Phone number and email address is required',
  email_required_phone_optional: 'Email address is required'
};

const getBothInputs = (customer_account_handle) => {
  switch (customer_account_handle) {
    case 'email_or_phone':
      return true;
    case 'email_and_phone':
      return true;
    case 'email_required_phone_optional':
      return true;
    default:
      return false;
  }
};

export const CreateUpdateCustomerForm: FC<CreateUpdateCustomerForm> = ({
  submitHandler,
  isUpdateForm,
  defaultValue
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const params = useParams();

  const {
    asset_list: {
      terms_and_conditions_url,
      privacy_policy_url,
      gender_select_label
    },
    behavior_list: { gender_selection_flag, customer_account_handle }
  } = useContext(AssetsContext);

  const onlyPhone = customer_account_handle === 'phone_only';
  const onlyEmail = customer_account_handle === 'email_only';
  const showPhoneInput = onlyPhone || getBothInputs(customer_account_handle);
  const showEmailInput = onlyEmail || getBothInputs(customer_account_handle);

  const updateMessages = getUpdateMessages(
    !!defaultValue?.phone,
    !!defaultValue?.email
  );

  const errorMessage = errorMessages[customer_account_handle];
  const userMessageForSignUp = singUpMessages[customer_account_handle];
  const userMessageForUpdate = updateMessages[customer_account_handle];

  const userMessage = isUpdateForm
    ? userMessageForUpdate
    : userMessageForSignUp;

  const [countryCode, setCountryCode] = useState<{
    countryCode: string;
    dialCode: string;
  }>({ countryCode: '', dialCode: '' });
  const [isLoading, setIsLoading] = useState(false);

  const defaultGender =
    gender_selection_flag === 'assumed_male'
      ? 'male'
      : gender_selection_flag === 'assumed_female'
      ? 'female'
      : null;

  const onSubmit = async (data) => {
    setIsLoading(true);
    let { feet, inch, weight } = data;
    if (data.heightMeasure === 'cm') {
      [feet, inch] = covertCmToFt(data.height);
    }

    if (data.weightMeasure === 'kgs') {
      weight = weight * onePnInKg;
    }

    const keys = Object.keys(data);
    keys.forEach((key) => {
      if (typeof data[key] === 'string') {
        data[key] = data[key].replaceAll(/\s+/g, '');
      }
    });

    await submitHandler({
      ...data,
      dateOfBirth: convertAgeToDate(data.dateOfBirth),
      email: data.email.toLowerCase(),
      height: converFtToInches(feet, inch),
      weight: Math.round(weight),
      phone: data.phone === countryCode.dialCode ? '' : data.phone
    });
    setIsLoading(false);
  };

  const termsAndConditionsLabel = (
    <div>
      I accept{' '}
      <a target="_blank" href={terms_and_conditions_url} rel="noreferrer">
        Terms of Use
      </a>{' '}
      and{' '}
      <a target="_blank" href={privacy_policy_url} rel="noreferrer">
        Privacy Policy
      </a>
      .
    </div>
  );

  const initialValues = getInitialValues(defaultGender || 'male');

  return (
    <Formik
      initialValues={
        defaultValue
          ? {
              ...defaultValue,
              dateOfBirth: convertDateToAge(defaultValue.dateOfBirth),
              ...convertInchesToFt(defaultValue.height),
              heightMeasure: 'fnIn',
              weightMeasure: 'lbs'
            }
          : { ...initialValues, ...convertInchesToFt(initialValues.height) }
      }
      validationSchema={getSchema(
        countryCode,
        errorMessage,
        getIsEmailRequired(customer_account_handle),
        getIsPhoneRequired(customer_account_handle),
        customer_account_handle === 'email_required_phone_optional'
      )}
      onSubmit={onSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        errors,
        touched,
        setFieldValue
      }) => {
        const {
          ftInputRef,
          inInputRef,
          cmInputRef,
          weightInputRef,
          ageInputRef,
          emailInputRef
        } = useCreateUpdateCustomerForm({
          setFieldValue,
          values
        });

        return (
          <Grid
            className={styles.formContainer}
            columnGap="3rem"
            rowGap="0.5rem"
            templateColumns="1fr 1fr 1fr 1fr 1fr 1fr"
          >
            <View columnSpan={[6, 6, 3]}>
              <TextInput
                label="First Name"
                type="text"
                name="firstName"
                placeholder="Enter first name"
                value={values.firstName}
                onBlur={handleBlur}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\s/g, '');
                  handleChange(e);
                }}
                hasError={touched.firstName && !!errors.firstName}
                errorMessage={errors.firstName as string}
              />
            </View>
            <View columnSpan={[6, 6, 3]}>
              <TextInput
                label="Last Name"
                name="lastName"
                placeholder="Enter last name"
                value={values.lastName}
                onBlur={handleBlur}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\s/g, '');
                  handleChange(e);
                }}
                hasError={touched.lastName && !!errors.lastName}
                errorMessage={errors.lastName as string}
              />
            </View>
            {showPhoneInput && (
              <View columnSpan={[6, 6, onlyPhone ? 6 : 3]}>
                <PhoneInputField
                  value={values.phone}
                  error={errors.phone as string}
                  touched={touched.phone as boolean}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  disabled={!!isUpdateForm && !!defaultValue?.phone}
                  setCountryCode={setCountryCode}
                />
              </View>
            )}
            {showEmailInput && (
              <View columnSpan={[6, 6, onlyEmail ? 6 : 3]}>
                <TextInput
                  ref={emailInputRef}
                  label="Email Address"
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={values.email.toLowerCase()}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\s/g, '');

                    const re = /^[A-Za-z][A-Za-z0-9._%+\-@]*$/;
                    if (e.target.value === '' || re.test(e.target.value)) {
                      handleChange(e);
                    } else {
                      e.target.value = values.email.toLowerCase();
                    }
                  }}
                  hasError={touched.email && !!errors.email}
                  errorMessage={errors.email as string}
                  disabled={!!isUpdateForm && !!defaultValue?.email}
                />
              </View>
            )}
            <View
              columnStart={
                customer_account_handle === 'email_required_phone_optional'
                  ? { base: 1, medium: onlyEmail ? 1 : 4 }
                  : 1
              }
              columnEnd={7}
            >
              <Typography component="h6">{userMessage}</Typography>
            </View>
            <View columnSpan={[6, 6, 3]}>
              <TextInput
                ref={ageInputRef}
                label="Age"
                placeholder="Age"
                type="text"
                name="dateOfBirth"
                value={values.dateOfBirth}
                className={styles.ageInput}
                onBlur={handleBlur}
                hasError={touched.dateOfBirth && !!errors.dateOfBirth}
                errorMessage={errors.dateOfBirth as string}
                onChange={(e) => {
                  if (e.target.value === '' || /^\d+$/g.test(e.target.value)) {
                    handleChange(e);
                  } else {
                    e.target.value = values.dateOfBirth;
                  }
                }}
              />
            </View>
            {!defaultGender && (
              <View columnSpan={[6, 6, 3]}>
                <RadioGroupField label={gender_select_label} name="gender">
                  <Flex>
                    <Radio
                      checked={values.gender === 'male'}
                      name="gender"
                      value="male"
                      onChange={handleChange}
                    >
                      Male
                    </Radio>
                    <Radio
                      checked={values.gender === 'female'}
                      name="gender"
                      value="female"
                      onChange={handleChange}
                    >
                      Female
                    </Radio>
                  </Flex>
                </RadioGroupField>
              </View>
            )}
            <View columnSpan={[6, 6, 3]} className="age-input">
              <Flex>
                <TextInput
                  ref={weightInputRef}
                  label="Weight"
                  type="text"
                  placeholder="Weight"
                  className={styles.input}
                  value={values.weight}
                  name="weight"
                  onBlur={handleBlur}
                  onChange={(e) => {
                    if (
                      e.target.value === '' ||
                      /^\d+$/g.test(e.target.value)
                    ) {
                      handleChange(e);
                    } else {
                      e.target.value = values.dateOfBirth;
                    }
                  }}
                  hasError={touched.weight && !!errors.weight}
                  errorMessage={errors.weight as string}
                />
                <RadioGroupField
                  label=""
                  name="weightMeasure"
                  direction="row"
                  className={styles.radioButtons}
                >
                  <Radio
                    checked={values.weightMeasure === 'lbs'}
                    name="weightMeasure"
                    value="lbs"
                    onChange={handleChange}
                  >
                    LB
                  </Radio>
                  <Radio
                    checked={values.weightMeasure === 'kgs'}
                    name="weightMeasure"
                    value="kgs"
                    onChange={handleChange}
                  >
                    KG
                  </Radio>
                </RadioGroupField>
              </Flex>
            </View>
            <View columnSpan={[6, 6, 3]}>
              <Flex>
                {values.heightMeasure === 'cm' ? (
                  <TextInput
                    ref={cmInputRef}
                    label="Height"
                    type="text"
                    placeholder="Height"
                    className={styles.input}
                    name="height"
                    value={values.height}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      if (
                        e.target.value === '' ||
                        /^\d+$/g.test(e.target.value)
                      ) {
                        handleChange(e);
                      } else {
                        e.target.value = values.dateOfBirth;
                      }
                    }}
                    hasError={touched.height && !!errors.height}
                    errorMessage={errors.height as string}
                  />
                ) : (
                  <Flex>
                    <TextInput
                      ref={ftInputRef}
                      label="Height"
                      type="text"
                      placeholder="Ft"
                      name="feet"
                      value={values.feet}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        if (
                          e.target.value === '' ||
                          /^\d+$/g.test(e.target.value)
                        ) {
                          handleChange(e);
                        } else {
                          e.target.value = values.dateOfBirth;
                        }
                      }}
                      hasError={touched.feet && !!errors.feet}
                      errorMessage={errors.feet as string}
                      className={styles.heightSmallInput}
                    />
                    <TextInput
                      ref={inInputRef}
                      label=""
                      type="text"
                      placeholder="In"
                      name="inch"
                      value={values.inch}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        if (
                          e.target.value === '' ||
                          /^\d+$/g.test(e.target.value)
                        ) {
                          handleChange(e);
                        } else {
                          e.target.value = values.dateOfBirth;
                        }
                      }}
                      hasError={touched.inch && !!errors.inch}
                      errorMessage={errors.inch as string}
                      className={classNames(
                        styles.heightSmallInput,
                        styles.heightSmallInputWithoutLabel
                      )}
                    />
                  </Flex>
                )}
                <RadioGroupField
                  label=""
                  name="weightMeasure"
                  direction="row"
                  className={styles.radioButtons}
                >
                  <Radio
                    checked={values.heightMeasure === 'fnIn'}
                    name="heightMeasure"
                    value="fnIn"
                    onChange={handleChange}
                  >
                    FT/IN
                  </Radio>
                  <Radio
                    checked={values.heightMeasure === 'cm'}
                    name="heightMeasure"
                    value="cm"
                    onChange={handleChange}
                  >
                    CM
                  </Radio>
                </RadioGroupField>
              </Flex>
            </View>
            {!isUpdateForm && (
              <View columnSpan="6" className={styles.termsAndConditions}>
                <CheckboxField
                  value={''}
                  label={termsAndConditionsLabel}
                  name="termsAndConditions"
                  hasError={
                    touched.termsAndConditions && !!errors.termsAndConditions
                  }
                  onChange={handleChange}
                  required
                  errorMessage="You must agree before submitting."
                />
              </View>
            )}
            <View
              columnSpan="6"
              className={classNames(
                styles.buttonContainer,
                isUpdateForm && styles.buttonContainerPadding
              )}
            >
              {isUpdateForm && (
                <Button
                  buttonType="dark"
                  text="Back to user page"
                  className={styles.backButton}
                  onClick={() => navigate(`/customers/${params.customerId}`)}
                />
              )}
              <Button
                isLoading={isLoading}
                buttonType="secondary"
                text={isUpdateForm ? 'Update' : 'Sign up'}
                className={styles.submitButton}
                onClick={() => handleSubmit()}
              />
            </View>
          </Grid>
        );
      }}
    </Formik>
  );
};
